import React, { useEffect } from 'react';
import { MdArrowForward } from 'react-icons/md';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { errorsSelector, otpSelector } from '@src/features/customers';
import { useLogin } from '@src/features/customers/auth/login';
import { convertGeneralContextErrorToHookForm, getFormattedTime } from '@src/common';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import Button from '@src/components/ui2/Button';
import { CodeInput } from '@src/components/ui2/CodeInput';
import { OtpMessage, ResendCode, SubTitle } from './styles';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from './schema';
import useAppEnvironment from '@src/hooks/useAppEnvironment';

type OtpFormProps = {
  email: string;
  onSuccess?: () => void;
  onFail?: () => void;
};

const OtpForm = (props: OtpFormProps) => {
  const { email, onSuccess = () => {}, onFail = () => {} } = props;

  const { i18n } = useTranslate();
  const appEnv = useAppEnvironment();
  const {
    submitOtp,
    sendOtp: sendOtpAction,
    otpResend: { minutes, seconds, canResend },
  } = useLogin();

  const { status, loading } = useAppSelector(otpSelector);
  const contextErrors = useAppSelector(errorsSelector);

  const { handleSubmit, control, formState, setError } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema(i18n)),
  });
  const { errors } = formState;

  const sendOtp = () => {
    sendOtpAction(email);
  };

  const submit = (payload: FieldValues) => {
    submitOtp(email, payload['otp']);
  };

  useEffect(() => {
    sendOtp();
  }, []);

  useEffect(() => {
    convertGeneralContextErrorToHookForm('otp', setError, contextErrors, 'otp');
    convertGeneralContextErrorToHookForm('otp', setError, contextErrors, 'login');
  }, [contextErrors]);

  useEffect(() => {
    switch (status) {
      case 'success':
        onSuccess();
        break;
      case 'error':
        onFail();
        break;

      default:
        break;
    }
  }, [status]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <SubTitle>{i18n('direct_link_auth_login_otp_form_title')}</SubTitle>

      <Controller
        name='otp'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <CodeInput
            value={value}
            onChange={(e) => onChange(e)}
            label={i18n('direct_link_auth_login_otp_form_code_label')}
            name={name}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            disabled={loading}
            {...rest}
          />
        )}
      />

      <Button fullWidth type='submit' loading={loading}>
        {i18n('direct_link_auth_login_otp_form_login')} <MdArrowForward />
      </Button>

      {loading ? (
        <OtpMessage>{i18n('direct_link_auth_login_otp_form_sending_code')}</OtpMessage>
      ) : !canResend ? (
        <OtpMessage>
          {i18n('direct_link_auth_login_otp_form_code_expires')} {getFormattedTime(minutes)}:{getFormattedTime(seconds)}
        </OtpMessage>
      ) : (
        <ResendCode onClick={() => sendOtp()}>{i18n('direct_link_auth_login_otp_form_resend_code')}</ResendCode>
      )}
    </form>
  );
};

export default OtpForm;
